import React, { useEffect, useState } from "react";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { useTranslation } from "../../../utils/useTranslation";
import { View, Text } from "../../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import {
  Dimensions,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { AntDesign, Ionicons } from "@expo/vector-icons";
import { default as Modal } from "../../../components/custom/Modal";
import { reserveStockModel } from "../../../models/reportsModel";

interface RouteParams {
  order: reserveStockModel;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

interface ReserveStockDetailsScreenProps {
  order?: reserveStockModel;
  onBackPress?: () => void;
}

const ReserveStockDetailsScreen = ({ order: propOrder, onBackPress }: ReserveStockDetailsScreenProps = {}) => {
  const [modalVisible, setModalVisible] = useState(true);

  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const routeOrder = route?.params?.order;
  const order = propOrder || routeOrder || {} as reserveStockModel;
  const { t } = useTranslation();
  const { colors, isDark } = useAppTheme();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const navigation = useNavigation();

  useEffect(() => {
    if (!modalVisible) {
      if (onBackPress) {
        onBackPress();
      } else {
        if (navigation.canGoBack()) {
          navigation.goBack();
        }
      }
    }
  }, [modalVisible]);


  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || Object.keys(order).length === 0) {
      navigation.navigate("ReserveStock" as never);
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  const handleBack = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      navigation.navigate("ReserveStock" as never);
    }
  };

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
          >
            <AntDesign name="arrowleft" size={24} color={isDark ? colors.text : "black"} />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text selectable={true} style={styles.headerTitle}>
              {`${t("itemCode")}: ${order?.ItemCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const detailItems: DetailItem[] = [
      { label: "Order Number", value: order?.DocNum || "-" },
      { label: "Customer Code", value: order?.CardCode || "-" },
      { label: "Customer Name", value: order?.CardName || "-" },
      { label: "Address", value: order?.Address2 || "-" },
      { label: "Warehouse", value: order?.WhsName || order?.WhsCode || "-" },
      { label: "Item Code", value: order?.ItemCode || "-" },
      { label: "Description", value: order?.Dscription || "-" },
      { 
        label: "Order Reserved", 
        value: order?.["Open Qty PCS"]?.toString() || "0",
        supportingValue: order?.PerCtns ? `${Number(order?.["Open Qty PCS"] || 0) / Number(order?.PerCtns)} CTNs` : undefined
      },
      { label: "Sales", value: order?.SlpName || "-" },
      { label: "Order Date", value: order?.["Order Date"] ? new Date(order?.["Order Date"]).toLocaleDateString('en-GB') : "-" },
      { label: "Valid until", value: order?.["Valid until"] ? new Date(order?.["Valid until"]).toLocaleDateString('en-GB') : "-" },
      { label: "Doc Status", value: order?.["Doc Status"] || "-" },
    ];

    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text selectable={true} style={styles.title}>{order?.ItemCode || ""}</Text>
          <Text selectable={true} style={styles.subtitle}>{order?.Dscription || ""}</Text>
        </View>
        <View style={styles.body}>
          {detailItems.map((item, index) => (
            <View key={index} style={styles.detailRow}>
              <Text selectable={true} style={styles.label}>{item.label}:</Text>
              <Text selectable={true} style={styles.value}>{item.value}</Text>
              {item.supportingValue && (
                <Text selectable={true} style={styles.supporting}>
                  ({item.supportingValue})
                </Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  
  const ModalHeader = () => (
    <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", padding: 16, backgroundColor: isDark ? colors.surfaceSubtle : "#fff", borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomWidth: 1, borderColor: isDark ? colors.border : "#e0e0e0" }}>
      <Text style={{ fontSize: 18, fontWeight: "bold", color: isDark ? colors.text : "#0F172A" }}>{`${t("itemCode")}: ${order?.ItemCode || ""}`}</Text>
      <TouchableOpacity onPress={() => setModalVisible(false)} style={{ padding: 4 }}>
        <Ionicons name="close" size={24} color={isDark ? colors.text : "#333"} />
      </TouchableOpacity>
    </View>
  );

  const ModalContentInner = (props: any) => (
    <View style={{ flex: 1, width: '100%', backgroundColor: isDark ? colors.background : '#fff', borderRadius: 16, overflow: 'hidden' }}>
      <ModalHeader />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={[styles.contentContainer, isMobileView && styles.contentContainerMobile]}>
          <View style={[styles.orderDetails, isMobileView && styles.orderDetailsMobile]}>
            <View style={[styles.detailBox, isMobileView && styles.detailBoxMobile]}>
              <ProductDetails />
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );

  return (
    <Modal.ModalContainer
      modalVisible={modalVisible}
      setModalVisible={setModalVisible}
      childern={ModalContentInner}
    />
  );
};
function createStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    container: {
      flexGrow: 1,
      backgroundColor: isDark ? colors.background : "#fff",
    },
    contentContainer: {
      padding: 16,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    contentContainerMobile: {
      flexDirection: "column",
    },
    orderDetails: {
      flex: 1,
      maxWidth: "100%",
    },
    orderDetailsMobile: {
      flex: 0,
      maxWidth: "100%",
      marginBottom: 16,
    },
    detailBox: {
      borderRadius: 14,
      padding: 16,
      minHeight: "100%",
    },
    detailBoxMobile: {
      height: "auto",
    },
    detailRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? colors.border : "#e0e0e0",
    },
    verticalLine: {
      width: 1,
      backgroundColor: isDark ? colors.border : "#e3e3e3",
      marginHorizontal: 16,
    },
    drawerHeaderStyle: {
      width: "100%",
      zIndex: 999,
      backgroundColor: isDark ? colors.surface : "#fff",
      height: 56,
      borderBottomWidth: 1,
      borderColor: isDark ? colors.border : "#e0e0e0",
      justifyContent: "center",
      paddingHorizontal: 16,
    },
    headerRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    backButton: {
      paddingVertical: 8,
      paddingRight: 16,
    },
    headerTitleContainer: {
      flex: 1,
    },
    headerTitle: {
      fontWeight: "700",
      fontSize: 16,
      userSelect: "text" as any,
      color: isDark ? colors.text : "#0F172A",
    },
    // Product Details Styles
    productDetailsContainer: {
      flex: 1,
    },
    header: {
      marginBottom: 16,
    },
    title: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: 4,
      userSelect: "text" as any,
      color: isDark ? colors.text : "#0F172A",
    },
    subtitle: {
      fontSize: 14,
      color: isDark ? colors.textSecondary : "#666",
      marginBottom: 16,
      userSelect: "text" as any,
    },
    body: {
      flex: 1,
    },
    label: {
      fontWeight: "600",
      fontSize: 14,
      flex: 1,
      userSelect: "text" as any,
      color: isDark ? colors.textSecondary : "#333",
    },
    value: {
      fontSize: 14,
      color: isDark ? colors.text : "#333",
      flex: 1,
      textAlign: "right",
      userSelect: "text" as any,
    },
    supporting: {
      fontSize: 12,
      color: isDark ? colors.textMuted : "#888",
      marginLeft: 8,
      userSelect: "text" as any,
    },
  });
}

export default ReserveStockDetailsScreen;
